'use client'

import { FormattedMessage } from 'react-intl'
import { find } from 'lodash'

import { Button, Icon, Text } from '@vinted/web-ui'
import { InfoCircle16 } from '@vinted/monochrome-icons'

import {
  cannotBundleButtonClickEvent,
  ReasonForNotBundling,
} from '@marketplace-web/bundles/items-bundle-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import { Screen } from 'constants/tracking/screens'

import { getIsBundlingAllowed } from '../../pages/BundleItems/utils'

type BundleItemFooterProps = {
  item: ProductItemModel
  items: Array<ProductItemModel>
  onClick: (item: ProductItemModel, index: number) => () => void
  sellerId: number
  itemIndex: number
  selectedItems: Array<ProductItemModel>
  isSelectedItemLimitReached: () => boolean
  bundleTrackingId: string | null
  onCantBundleButtonClick: () => void
  isProfilePage?: boolean
  screen: string
}

const BundleItemFooter = ({
  item,
  items,
  onClick,
  sellerId,
  itemIndex,
  selectedItems,
  isSelectedItemLimitReached,
  bundleTrackingId,
  onCantBundleButtonClick,
  isProfilePage = false,
  screen,
}: BundleItemFooterProps) => {
  const { track } = useTracking()
  const translate = useTranslate('items_multiselect')

  const itemSelected = !!find(selectedItems, { id: item.id })
  const addTextSuffix = isProfilePage ? 'add_to_bundle' : 'add'
  const buttonTextSuffix = itemSelected ? 'remove' : addTextSuffix
  const testIdSuffix = itemSelected ? 'remove' : 'add'
  const isButtonDisabled = isSelectedItemLimitReached() && !itemSelected
  const trackingData = {
    listingId: screen === Screen.UserProfile ? item.id.toString() : item.id,
    sellerId: screen === Screen.UserProfile ? sellerId.toString() : sellerId,
    reasonForNotBundling: ReasonForNotBundling.ShippingSize,
    screen,
    bundleSessionId: bundleTrackingId,
  }

  const handleCantBundleButtonClick = () => {
    onCantBundleButtonClick()
    track(cannotBundleButtonClickEvent(trackingData))
  }

  if (getIsBundlingAllowed({ item, bundleItems: items, selectedItemId: selectedItems[0]?.id })) {
    return (
      <Button
        text={<FormattedMessage id={`items_multiselect.actions.${buttonTextSuffix}`} />}
        theme={itemSelected ? 'warning' : 'primary'}
        size={Button.Size.Small}
        onClick={onClick(item, itemIndex)}
        disabled={isButtonDisabled}
        testId={`${testIdSuffix}-button`}
        aria={{
          'aria-label': itemSelected ? translate('a11y.remove') : undefined,
        }}
      />
    )
  }

  return (
    <Button
      text={<Text text={translate('actions.disabled')} as="span" type={Text.Type.Caption} />}
      styling={Button.Styling.Flat}
      size={Button.Size.Small}
      theme="muted"
      icon={<Icon name={InfoCircle16} />}
      iconPosition={Button.IconPosition.Right}
      testId="cant-bundle-button"
      onClick={handleCantBundleButtonClick}
    />
  )
}

export default BundleItemFooter
